import { Button, Form, Input, Select, Tag, message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../nestserver";
import { selectUser } from "../../../../redux/slices/auth.slice";
import { numberInputGuard } from "../../../../components/inputs/numberInputGuard";
import { ASSESSMENT_TYPES, labelsFor } from "./assessmentType";

function tagRender(props) {
  const { label, closable, onClose } = props;

  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
      className="flex items-center"
    >
      {label}
    </Tag>
  );
}

export default function CreateAssessmentForm({
  onCancel,
  subject_id,
  class_id,
  classesUrl = "/teachers/classes",
  type = ASSESSMENT_TYPES.exam,
}) {
  const labels = labelsFor(type);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);

  const { Option } = Select;
  const [classId, setClassId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);

  const onFinish = (values) => {
    const formData = new FormData();
    formData.append("type", type);
    formData.append("name", values.name);
    formData.append("total_marks", values.total_marks);
    formData.append("class", classId);
    formData.append("subject", subjectId);

    axios({
      method: "post",
      url: serverUrl + "/exam-v2/",
      data: formData,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        "Content-Type": "multipart/form-data",
      },
    })
      .then(() => {
        message.success(`${labels.one} Created`);
        queryClient.invalidateQueries("assessments");
        form.resetFields();
        onCancel(false);
      })
      .catch((e) => {
        const reason = e?.response?.data?.message;
        message.error(Array.isArray(reason) ? reason[0] : reason || e.message);
      });
  };

  // fetch Class
  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + classesUrl,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery(["classes", classesUrl], fetchClasses);

  // fetch subjects
  async function fetchSubjects() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/subjects/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const subjects = useQuery("subjects", fetchSubjects);

  useEffect(() => {
    form.resetFields();
  }, []);

  useEffect(() => {
    form.resetFields();
    if (subject_id) {
      setSubjectId(subject_id);
    }
    if (class_id) {
      setClassId(class_id);
    }
  }, [subject_id, class_id]);

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        name="basic"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{
          class: class_id,
          subject: subject_id,
        }}
      >
        <div>
          <Form.Item
            label={`${labels.one} Name`}
            name="name"
            rules={[
              {
                required: true,
                message: `Please input ${labels.one} Name !`,
              },
            ]}
          >
            <Input
              maxLength={130}
              style={{
                backgroundColor: "#FFFFFF",
              }}
              className="w-full py-2"
            />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label={`${labels.one} Total Marks`}
            name="total_marks"
            rules={[
              {
                required: true,
                message: `Please input ${labels.one} Total Marks!`,
              },
              {
                pattern: /^[1-9]\d*$/,
                message: "Please enter a positive number",
              },
            ]}
          >
            <Input
              type="number"
              min={1}
              style={{
                backgroundColor: "#FFFFFF",
              }}
              className="w-full py-2"
              {...numberInputGuard}
            />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label="Select Class"
            name="class"
            rules={[
              {
                required: true,
                message: "Please input Select Class!",
              },
            ]}
          >
            <Select
              showSearch
              showArrow
              disabled
              placeholder="Select Class"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
              className="w-full bg-gray-300"
              style={{
                border: "6px",
                backgroundColor: "#EBEBEB",
              }}
              onChange={(e) => setClassId(e)}
            >
              {classes.isSuccess &&
                classes?.data?.map((data) => {
                  return (
                    <Option key={data._id} value={data._id}>
                      {data.name}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
        </div>

        {classId && (
          <>
            <Form.Item label="Subject" name="subject">
              <Select
                showSearch
                showArrow
                disabled
                tagRender={tagRender}
                placeholder="Select Subject"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                style={{
                  border: "6px",
                  backgroundColor: "#EBEBEB",
                }}
                className="w-3/4"
                onChange={(e) => setSubjectId(e)}
              >
                {subjects.isSuccess &&
                  subjects?.data
                    ?.filter((s) => s.class?._id === classId)
                    ?.filter((s) =>
                      user?.teacher?.subjects
                        ? user.teacher.subjects.map((x) => x._id).includes(s._id)
                        : true,
                    )
                    ?.map((data) => {
                      return (
                        <Option key={data._id} value={data._id}>
                          {data.name}
                        </Option>
                      );
                    })}
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item className="mb-0">
          <div className="flex items-center  pt-8">
            <div className="inline-block ml-auto space-x-3">
              <Button
                type="dark"
                size="large"
                onClick={() => {
                  onCancel();
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" size="large" htmlType="submit">
                Save
              </Button>
            </div>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}
